export const KNOWN_SIZING_TYPES = [
  'alpha',
  'waist_numeric',
  'waist_length',
  'barleycorn',
  'paris_point',
  'women_numeric',
  'ring_numeric',
] as const;

export type KnownSizingType = (typeof KNOWN_SIZING_TYPES)[number];
export type ItemSizingType = KnownSizingType | string;

export interface SizingTypeDefinition {
  value: KnownSizingType;
  label: string;
  example: string;
}

export interface SizingConvention {
  id: string;
  countrySize: string;
  sizeValue: string;
  sortOrder: number | null;
  sizeSystem: ItemSizingType;
}

const SIZING_OPTIONS_STORAGE_KEY = 'vadauro:sizing-options-by-type';
const DISALLOWED_SIZE_VALUES = new Set(['custom']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const SIZING_TYPE_DEFINITIONS: SizingTypeDefinition[] = [
  { value: 'alpha', label: 'Alpha', example: 'S, M, L' },
  { value: 'waist_numeric', label: 'Waist Numeric', example: '31, 32, 33' },
  { value: 'waist_length', label: 'Waist Length', example: '31X31, 32X33, 32X34' },
  { value: 'barleycorn', label: 'Barleycorn', example: '8.5, 9, 9.5' },
  { value: 'paris_point', label: 'Paris Point', example: '44, 45, 46' },
  { value: 'women_numeric', label: "Women's Numeric", example: '0, 2, 4' },
  { value: 'ring_numeric', label: 'Ring Numeric', example: '4, 4.5, 5' },
];

function parseJsonValue(value: unknown): unknown {
  let current = value;

  for (let depth = 0; depth < 4 && typeof current === 'string'; depth += 1) {
    const trimmedValue = current.trim();
    if (!trimmedValue) {
      return current;
    }

    try {
      current = JSON.parse(trimmedValue) as unknown;
    } catch {
      return current;
    }
  }

  return current;
}

export function unwrapSizingConventionsPayload(payload: unknown): unknown {
  let current = parseJsonValue(payload);

  for (let index = 0; index < 6; index += 1) {
    if (!isRecord(current)) {
      break;
    }

    if (Array.isArray(current.sizing_conventions)) {
      return current;
    }

    if (current.data !== undefined) {
      current = parseJsonValue(current.data);
      continue;
    }

    if (current.body !== undefined) {
      current = parseJsonValue(current.body);
      continue;
    }

    if (current.message !== undefined) {
      current = parseJsonValue(current.message);
      continue;
    }

    break;
  }

  return current;
}

export function normalizeSizingType(value: unknown): ItemSizingType | '' {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toLowerCase();
}

export function normalizeSizingValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

export function isAllowedSizingValue(value: unknown) {
  const normalizedValue = normalizeSizingValue(value);
  return Boolean(normalizedValue) && !DISALLOWED_SIZE_VALUES.has(normalizedValue.toLowerCase());
}

export function extractSizingConventions(payload: unknown): SizingConvention[] {
  const entries = Array.isArray(payload)
    ? payload
    : isRecord(payload) && Array.isArray(payload.sizing_conventions)
      ? payload.sizing_conventions
      : [];

  if (entries.length === 0) {
    return [];
  }

  const seenKeys = new Set<string>();

  return entries.flatMap((entry) => {
    if (!isRecord(entry)) {
      return [];
    }

    const sizeSystem = normalizeSizingType(entry.size_system);
    const sizeValue = normalizeSizingValue(entry.size_value);

    if (!sizeSystem || !isAllowedSizingValue(sizeValue)) {
      return [];
    }

    const key = `${sizeSystem}\u0000${sizeValue}`;
    if (seenKeys.has(key)) {
      return [];
    }

    seenKeys.add(key);
    const sortOrder = typeof entry.sort_order === 'number'
      ? entry.sort_order
      : Number.isFinite(Number(entry.sort_order))
        ? Number(entry.sort_order)
        : null;

    return [{
      id: typeof entry.id === 'string' ? entry.id : key,
      countrySize: normalizeSizingValue(entry.country_size),
      sizeValue,
      sortOrder,
      sizeSystem,
    }];
  });
}

export function buildSizingOptionsByType(conventions: SizingConvention[]) {
  const optionsByType = new Map<ItemSizingType, string[]>();

  conventions
    .slice()
    .sort((left, right) => {
      const leftSort = left.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const rightSort = right.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (leftSort !== rightSort) {
        return leftSort - rightSort;
      }

      return left.sizeValue.localeCompare(right.sizeValue, undefined, { numeric: true, sensitivity: 'base' });
    })
    .forEach((convention) => {
      const currentOptions = optionsByType.get(convention.sizeSystem) ?? [];
      if (isAllowedSizingValue(convention.sizeValue) && !currentOptions.includes(convention.sizeValue)) {
        currentOptions.push(convention.sizeValue);
      }
      optionsByType.set(convention.sizeSystem, currentOptions);
    });

  return optionsByType;
}

export function persistSizingOptionsByType(optionsByType: Map<ItemSizingType, string[]>) {
  if (typeof window === 'undefined') {
    return;
  }

  const serializedEntries = Array.from(optionsByType.entries()).filter(([, values]) => values.length > 0);
  window.sessionStorage.setItem(SIZING_OPTIONS_STORAGE_KEY, JSON.stringify(serializedEntries));
}

export function readPersistedSizingOptionsByType(): Map<ItemSizingType, string[]> {
  if (typeof window === 'undefined') {
    return new Map();
  }

  const rawValue = window.sessionStorage.getItem(SIZING_OPTIONS_STORAGE_KEY);
  if (!rawValue) {
    return new Map();
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;
    if (!Array.isArray(parsedValue)) {
      return new Map();
    }

    return new Map(
      parsedValue.flatMap((entry) => {
        if (!Array.isArray(entry) || entry.length !== 2) {
          return [];
        }

        const [key, values] = entry;
        if (typeof key !== 'string' || !Array.isArray(values)) {
          return [];
        }

        return [[key, values.filter((value): value is string => typeof value === 'string' && isAllowedSizingValue(value))]];
      })
    );
  } catch {
    return new Map();
  }
}

export function getSizingTypeLabel(value: ItemSizingType) {
  const knownDefinition = SIZING_TYPE_DEFINITIONS.find((definition) => definition.value === value);
  if (knownDefinition) {
    return knownDefinition.label;
  }

  return value
    .split(/_+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function getSizingTypeDescription(value: ItemSizingType) {
  const knownDefinition = SIZING_TYPE_DEFINITIONS.find((definition) => definition.value === value);
  if (knownDefinition) {
    return `${knownDefinition.label} (${knownDefinition.example})`;
  }

  return getSizingTypeLabel(value);
}

export function getOrderedSizingTypes(optionsByType: Map<ItemSizingType, string[]>) {
  const availableTypes = new Set(optionsByType.keys());
  const orderedKnownTypes = SIZING_TYPE_DEFINITIONS
    .map((definition) => definition.value)
    .filter((value) => availableTypes.has(value));
  const orderedKnownTypeSet = new Set<ItemSizingType>(orderedKnownTypes);
  const extraTypes = Array.from(availableTypes).filter((value) => !orderedKnownTypeSet.has(value));

  return [...orderedKnownTypes, ...extraTypes];
}

export function getSizingOptionsWithCurrentValue(options: string[], currentValue: unknown) {
  const normalizedCurrentValue = normalizeSizingValue(currentValue);
  if (!isAllowedSizingValue(normalizedCurrentValue) || options.includes(normalizedCurrentValue)) {
    return options;
  }

  return [normalizedCurrentValue, ...options];
}
